import { reportDashboardMetricValues } from '../dashboard/reportMetricValues';
import type { DashboardDateRange } from '@/components/dashboard/model';
import { createReportExplorerClient } from '../reporting/reportExplorer/api';
import type { ReportFilter } from '../reporting/reportExplorer/model';
import {
  ReportDimension,
  ReportExplorerScope,
  ReportMetric,
  ReportType,
} from '../reporting/reportExplorer/reportEnums';
import { DashboardMetric, type DashboardTimelinePoint } from '../dashboard/model';

export const campaignOverviewMetrics = [
  DashboardMetric.AdImpressions,
  DashboardMetric.Revenue,
  DashboardMetric.Rpm,
  DashboardMetric.AdClicks,
];
export type CampaignOverviewFilter = Pick<
  ReportFilter,
  'agencyIds' | 'msBrandIds' | 'msCampaignIds'
>;

export function createCampaignOverviewClient(
  options: Parameters<typeof createReportExplorerClient>[0],
) {
  const api = createReportExplorerClient(options);
  return {
    async report(range: DashboardDateRange, filter: CampaignOverviewFilter, agencyId?: string) {
      const hourly = range.dateFrom === range.dateTo;
      const response = await api.run(
        {
          source: ReportType.Exchange,
          dimensions: hourly ? [ReportDimension.Day, ReportDimension.Hour] : [ReportDimension.Day],
          metrics: [
            ReportMetric.Day,
            ...(hourly ? [ReportMetric.Hour] : []),
            ReportMetric.AdImpressionsLi,
            ReportMetric.RevenueLi,
            ReportMetric.RpmLi,
            ReportMetric.AdClicks,
          ],
          filters: {
            ...filter,
            dateRange: null,
            dateFrom: range.dateFrom,
            dateTo: range.dateTo,
            exchangeNetworkOnly: false,
          },
        },
        agencyId ? ReportExplorerScope.Agency : ReportExplorerScope.Standard,
        agencyId,
      );
      const points: DashboardTimelinePoint[] = response.report.flatMap((row) => {
        if (typeof row.day !== 'string') return [];
        if (hourly && typeof row.hour !== 'string' && typeof row.hour !== 'number') return [];
        return [
          {
            day: row.day,
            ...(hourly ? { hour: row.hour as string | number } : {}),
            ...reportDashboardMetricValues(row),
          },
        ];
      });
      points.sort(
        (a, b) => a.day.localeCompare(b.day) || Number(a.hour ?? 0) - Number(b.hour ?? 0),
      );
      return {
        total: reportDashboardMetricValues(response.total),
        hours: hourly ? points : [],
        totalsByDay: hourly ? [] : points,
      };
    },
  };
}
